import { Flex, Loader, Paper, Text, Title } from "@mantine/core";
import { useGetBookingStatus } from "../../hooks/api";
import { useEffect, useState } from "react";
import { useUpdateCustomerData } from "../../hooks/ui/";
import dayjs from "dayjs";
import TimeForAdvicePaper from "../timeForAdvicePaper";
import { isFullCredentials } from "../../hooks/ui/customerDataStore";
import { isMobileDevice } from "../../utils/useDeviceDetection";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";

const BookingConfirmationPaper = ({ bookingId }: { bookingId?: string }) => {
  const { customerData } = useUpdateCustomerData();
  const bookingIdToUse = bookingId ? bookingId : customerData.booking.bookingId;
  const [isQueryEnabled, setIsQueryEnabled] = useState(true);

  const { data, isSuccess, isError, isLoading } = useGetBookingStatus({
    bookingId: bookingIdToUse,
    enabled:
      isQueryEnabled &&
      isFullCredentials(customerData.credentials) &&
      !!bookingIdToUse,
    credentials: customerData.credentials,
  });

  const isMobile = isMobileDevice();

  useEffect(() => {
    if (data?.status || isError) {
      setIsQueryEnabled(false);
    }
  }, [data?.status, isError]);

  const today = dayjs();
  const bookingDate = dayjs(data?.startsAt);
  const isSameDay = data?.startsAt && today.isSame(bookingDate, "day");
  const isAfter = data?.startsAt && today.isAfter(bookingDate, "day");

  // Format date and time in Swedish if data exists
  const formattedDate = data?.startsAt
    ? dayjs(data.startsAt).format("dddd D MMMM YYYY")
    : "";

  const formattedTime = data?.startsAt
    ? dayjs(data.startsAt).format("HH:mm")
    : "";

  if (isSuccess && (isSameDay || isAfter)) {
    return (
      <TimeForAdvicePaper
        message={
          isSameDay
            ? `Det är dags för rådgivning idag kl ${formattedTime}`
            : "Det är dags för rådgivning!"
        }
      />
    );
  }

  return (
    <Paper
      px={"lg"}
      py={isMobile ? "1rem" : "lg"}
      radius={"lg"}
      bg={"var(--wallet-color-masterbrand-accent-yellow)"}
    >
      {(isLoading || (!isSuccess && !isError)) && (
        <Flex justify={"center"}>
          <Loader c="var(--wallet-color-neutrals-black)" />
        </Flex>
      )}
      {isError && (
        <Title
          c="var(--wallet-color-neutrals-black)"
          order={4}
          ff={"var(--wallet-font-family-sambla-sans)"}
        >
          Hoppsan! Något gick fel, vänligen försök igen..
        </Title>
      )}
      {isSuccess &&
        !isLoading &&
        (!(data?.status === "confirmed") || isAfter) && (
          <>
            <Title
              c={"var(--wallet-color-neutrals-black)"}
              order={4}
              ff={"var(--wallet-font-family-sambla-sans)"}
            >
              En rådgivare kommer att ringa dig för att boka ett möte
            </Title>
            <Text>
              Nu går vi igenom din situation och ser hur vi kan förbättra ditt
              sparande. När vi har allt vi behöver kommer du att bli uppringd av
              oss.
            </Text>
          </>
        )}
      {isSuccess && !isSameDay && !isAfter && data?.status === "confirmed" && (
        <>
          <Title
            c={"var(--wallet-color-neutrals-black)"}
            order={4}
            mb={"lg"}
            ff={"var(--wallet-font-family-sambla-sans)"}
          >
            Vi ringer dig {formattedDate} klockan {formattedTime}
          </Title>
          <Text mb={"lg"}>
            Din rådgivare (
            <span className={REPLAY_BLOCK_CLASS}>{data.advisorName}</span>)
            kommer att gå igenom din pension tillsammans med dig och ge råd
            kring hur du kan förbättra ditt sparande. För att underlätta
            rådgivningen ser vi gärna att du har tillgång till en dator under
            samtalet.
          </Text>
        </>
      )}
    </Paper>
  );
};

export default BookingConfirmationPaper;
